/**
 * Afrikaans: Signing in and getting started
 *
 * What this file covers: Sign in, sign up, password reset, two-step sign-in, email verification, the data
 * processing agreement and the first-run onboarding.
 *
 * Tone: Warm, calm and reassuring, in very plain words: this is a business owner's first impression. Security
 * messages stay exact (a wrong code is wrong, an expired link is expired). The agreement text is legal:
 * translate it faithfully, never shorten or soften it.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const signIn: Catalog = {
  'auth.layout.tagline': 'Gesprekke, uitsendings, outomatisering en fakturering in een konsole.',
  'auth.common.password': 'Wagwoord',
  'auth.common.codeMismatch': 'Daardie kode het nie gepas nie.',
  'auth.common.backToSignIn': 'Terug na aanmelding',
  'auth.login.description': 'Teken in by jou RepletoryReach-rekening.',
  'auth.login.newHere': 'Nuut hier?',
  'auth.login.createAccount': 'Skep ’n rekening',
  'auth.login.forgotPassword': 'Wagwoord vergeet?',
  'auth.login.failed': 'Aanmelding het misluk.',
  'auth.login.twofa.hint': 'Voer die 6-syfer-kode uit jou verifikasieprogram in, of ’n herstelkode.',
  'auth.login.twofa.form.code': 'Verifikasiekode',
  'auth.login.twofa.verify': 'Verifieer',
  'auth.signup.title': 'Skep jou rekening',
  'auth.signup.description': 'Stel jou organisasie op en begin binne minute boodskappe stuur.',
  'auth.signup.haveAccount': 'Het jy reeds ’n rekening?',
  'auth.signup.form.orgName': 'Organisasienaam',
  'auth.signup.form.orgNamePlaceholder': 'Acme Coffee Roasters',
  'auth.signup.form.workEmail': 'Werk-e-pos',
  'auth.signup.form.passwordHint': 'Ten minste 12 karakters. ’n Kort sin van jou eie werk goed; geen simbole nodig nie.',
  'auth.signup.submit': 'Skep rekening',
  'auth.signup.failed': 'Registrasie het misluk.',
  'auth.forgot.title': 'Stel jou wagwoord terug',
  'auth.forgot.description': 'Ons stuur vir jou ’n e-posskakel om ’n nuwe wagwoord te kies.',
  'auth.forgot.submit': 'Stuur terugstelskakel',
  'auth.forgot.sent.title': 'Kyk in jou inkassie',
  'auth.forgot.sent.body': 'As daar ’n rekening vir {{email}} bestaan, is ’n wagwoordterugstelskakel op pad. Die skakel verval oor 1 uur.',
  'auth.reset.title': 'Kies ’n nuwe wagwoord',
  'auth.reset.description': 'Kies ’n sterk wagwoord vir jou rekening.',
  'auth.reset.form.newPassword': 'Nuwe wagwoord',
  'auth.reset.form.confirmPassword': 'Bevestig wagwoord',
  'auth.reset.submit': 'Stel wagwoord terug',
  'auth.reset.errors.tooShort': 'Die wagwoord moet ten minste 12 karakters hê.',
  'auth.reset.errors.mismatch': 'Wagwoorde stem nie ooreen nie.',
  'auth.reset.errors.invalidLink': 'Hierdie terugstelskakel is ongeldig. Vra ’n nuwe een aan.',
  'auth.reset.failed': 'Kon nie die wagwoord terugstel nie.',
  'auth.reset.toast.done': 'Jou wagwoord is teruggestel. Meld met jou nuwe wagwoord aan.',
  'auth.reset.toast.doneTitle': 'Wagwoord teruggestel',
  'auth.verify.title': 'Verifieer jou e-pos',
  'auth.verify.description': 'Voer die 6-syfer-kode in wat ons vir jou gestuur het.',
  'auth.verify.form.code': '6-syfer-kode',
  'auth.verify.submit': 'Verifieer e-pos',
  'auth.verify.resend': 'Stuur kode weer',
  'auth.verify.codeHint': 'Die kode verval 15 minute nadat dit gestuur is.',
  'auth.verify.backToDashboard': 'Terug na paneelbord',
  'auth.verify.toast.done': 'Jou e-pos is geverifieer.',
  'auth.verify.toast.resent': '’n Nuwe kode is op pad.',
  'auth.verify.toast.resentTitle': 'Kode gestuur',
  'auth.verify.toast.resendFailed': 'Kon nie weer stuur nie.',
  'auth.verify.toast.resendFailedTitle': 'Herstuur het misluk',
  'auth.mfa.title': 'Tweefaktor-aanmelding',
  'auth.mfa.description': 'Gaan voort met jou aanmelding vanaf die aanmeldskerm: jy word daar vir jou verifikasiekode gevra.',
  'auth.mfa.goToSignIn': 'Gaan na aanmelding',
  'onboarding.title': 'Welkom by RepletoryReach',
  'onboarding.description': '’n Paar vinnige stappe en jy is gereed om jou kliënte op WhatsApp te bereik.',
  'onboarding.stepIndicator': 'Stap {{current}} van {{total}} · {{label}}',
  'onboarding.skipSetup': 'Slaan opstelling oor',
  'onboarding.skipStep': 'Slaan hierdie stap oor',
  'onboarding.toast.codeResent': 'ŉ Nuwe kode is op pad na {{email}}.',
  'onboarding.steps.number.returnHint': 'Jou plek is gestoor, so jy kan ŉ nommer aanheg en hierheen terugkom.',
  'onboarding.steps.verify.label': 'Verifieer e-pos',
  'onboarding.steps.verify.title': 'Verifieer jou e-pos',
  'onboarding.steps.verify.body': 'Ons het ’n 6-syfer-kode aan {{email}} gestuur.',
  'onboarding.steps.verify.form.code': 'Verifikasiekode',
  'onboarding.steps.verify.resend': 'Stuur kode weer',
  'onboarding.steps.verify.skip': 'Slaan vir eers oor',
  'onboarding.steps.verify.confirm': 'Verifieer',
  'onboarding.steps.region.label': 'Streek',
  'onboarding.steps.region.title': 'Waar bedryf jy?',
  'onboarding.steps.region.description': 'Dit stel jou geldeenheid, tydsone en betaalpoort.',
  'onboarding.steps.region.form.country': 'Land',
  'onboarding.steps.region.country.za': 'Suid-Afrika (ZAR)',
  'onboarding.steps.region.country.ng': 'Nigerië (NGN)',
  'onboarding.steps.region.country.ke': 'Kenia (KES)',
  'onboarding.steps.name.label': 'Jy en jou besigheid',
  'onboarding.steps.name.title': 'Wat moet ons jou en jou besigheid noem?',
  'onboarding.steps.name.form.displayName': 'Jou naam',
  'onboarding.steps.name.form.displayNamePlaceholder': 'bv. Thandi N.',
  'onboarding.steps.number.label': 'WhatsApp-nommer',
  'onboarding.steps.number.title': 'Heg ’n WhatsApp-nommer aan',
  'onboarding.steps.number.description': 'Koppel jou Meta-foonnommer sodat jy kan stuur en ontvang. Jy kan dit nou doen of enige tyd vanaf die Nommers-bladsy.',
  'onboarding.steps.number.notice': 'Meta Embedded Signup (selfdienskoppeling) kom sodra jou Meta Business-toepassing goedgekeur is. Heg tot dan ’n nommer handmatig met sy Meta phone-number-id aan.',
  'onboarding.steps.number.openNumbers': 'Open Nommers',
  'onboarding.steps.done.label': 'Klaar',
  'onboarding.steps.done.title': 'Jy is heeltemal gereed',
  'onboarding.steps.done.description': 'Jou konsole is gereed. Voer kontakte in, skep ’n sjabloon en loop jou eerste uitsending: die paneelbord se kontrolelys wys die pad.',
  'onboarding.steps.done.goToDashboard': 'Gaan na paneelbord',
  'onboarding.toast.emailVerified': 'E-pos geverifieer.',
  'onboarding.toast.emailVerifiedTitle': 'Inlywing',
  'onboarding.toast.codeMismatch': 'Kode het nie gepas nie.',
  'onboarding.toast.codeMismatchTitle': 'Verifikasie',
  'onboarding.toast.saveFailed': 'Kon nie stoor nie.',
  'onboarding.toast.profileFailedTitle': 'Profiel',
  'onboarding.stepOf': 'Stap {{step}} van {{total}}',
  'onboarding.stepLabel': 'Stap',
  'onboarding.steps.verify.sentTo': 'Ons het ’n 6-syfer-kode aan {{email}} gestuur.',
  'auth.reset.errors.weak': 'Daardie wagwoord voldoen nie aan al die vereistes hieronder nie.',
  'auth.login.errors.badCredentials': 'Daardie e-pos en wagwoord pas nie by ʼn rekening nie. Kontroleer albei en probeer weer.',
  'auth.login.errors.wrongDomain': 'Hierdie aanmelding is vir besigheidsrekeninge. Platformpersoneel teken by die adminkonsole in.',
  'auth.login.errors.unavailable': 'Ons kon nie die aanmelddiens bereik nie. Kontroleer jou verbinding en probeer weer.',
  'auth.login.errors.badCode': 'Daardie kode is nie reg nie. Voer die huidige 6-syferkode in, of gebruik ʼn herstelkode.',
  'auth.verify.resent': 'Bevestigingse-pos gestuur: kyk in jou inkassie.',
  'auth.layout.docs': 'Dokumentasie',
  'auth.layout.status': 'Stelselstatus',
  'auth.layout.support': 'Ondersteuning',
  'auth.layout.copyright': '© {{year}} RepletoryReach. Alle regte voorbehou.',
  'auth.layout.theme': 'Tema',
  'auth.login.wrongDomain': 'Hierdie rekening teken by die RepletoryReach-adminkonsole in, nie hier nie.',
  'auth.login.tooMany': 'Te veel pogings. Wag ʼn minuut en probeer weer.',
  'auth.login.offline': 'Ons kon nie die aanmelddiens bereik nie. Kontroleer jou verbinding en probeer weer.',
  'auth.login.emailPlaceholder': 'jy@voorbeeld.com',
  'auth.login.passwordPlaceholder': 'Voer jou wagwoord in',
  'auth.login.useRecoveryCode': 'Gebruik ʼn herstelkode',
  'auth.login.useAuthCode': 'Gebruik ʼn verifikasiekode',
  'auth.login.recoveryCodeLabel': 'Herstelkode',
  'auth.signup.form.name': 'Jou naam',
  'auth.signup.form.namePlaceholder': 'Thandi Mokoena',
  'auth.signup.form.country': 'Land',
  'auth.signup.emailTaken': 'Daardie e-pos het reeds ʼn rekening.',
  'auth.signup.emailTaken.signIn': 'Teken in',
  'auth.signup.emailTaken.reset': 'Herstel jou wagwoord',
  'auth.forgot.errors.unavailable': 'Ons kon nie die e-pos nou stuur nie. Kontroleer jou verbinding en probeer weer.',
  'auth.forgot.sent.changeEmail': 'Gebruik ʼn ander e-pos',
  'auth.forgot.sent.resend': 'Stuur dit weer',
  'auth.forgot.sent.resendIn': 'Stuur dit weer oor {{seconds}}s',
  'auth.reset.form.passwordHint': 'Ten minste 12 karakters. ’n Kort sin van jou eie werk goed; geen simbole nodig nie.',
  'auth.reset.errors.requestNew': 'Vra ʼn nuwe skakel aan',
  'auth.reset.errors.emailMismatch': 'Daardie e-pos pas nie by hierdie herstelskakel nie.',
  'auth.reset.expiryNote': 'Herstelskakels verval een uur nadat hulle gestuur is.',
  'auth.verify.resendIn': 'Stuur weer oor {{seconds}}s',
  'auth.verify.alreadyVerified': 'Jou e-posadres is reeds bevestig.',
  'auth.verify.spamHint': 'Geen e-pos nie? Kyk in jou gemorspos.',
  'auth.mfa.backToSignIn': 'Gaan na aanmelding',
  'auth.common.or': 'of',
  'auth.login.title': 'Welkom terug 👋',
  'auth.layout.headline': 'Elke WhatsApp-gesprek wat jou span voer, op een plek.',
  'auth.layout.feature.inbox.title': 'Een gedeelde inkassie',
  'auth.layout.feature.inbox.body': 'Elke nommer wat jou besigheid gebruik, in een gespreksaansig. Ken toe, dra oor en hou reaksietye dop.',
  'auth.layout.feature.broadcasts.title': 'Uitsendings wat onttrekking respekteer',
  'auth.layout.feature.broadcasts.body': 'Segmenteer jou kontakte, demp die stuurtempo, en laat STOP iemand outomaties van die lys verwyder.',
  'auth.layout.feature.automation.title': 'Antwoorde sonder ʼn agent',
  'auth.layout.feature.automation.body': 'Bou ʼn vloei, grond dit op jou eie dokumente, en dra oor aan ʼn mens sodra dit nie meer kan help nie.',
  'auth.layout.feature.billing.title': 'Betaal vir boodskappe, nie sitplekke nie',
  'auth.layout.feature.billing.body': 'ʼn Kredietbeursie met ʼn volledige grootboek, BTW-geldige fakture en plaaslike betaalmetodes.',
  'auth.layout.trust': 'Gebou vir Suid-Afrikaanse spanne. POPIA-bewuste toestemmingsopsporing, ZAR-fakturering en SARS-geldige belastingfakture.',
  'auth.social.or': 'of',
  'auth.social.continueWith': 'Gaan voort met {{provider}}',
  'auth.social.provider': 'u verskaffer',
  'auth.social.hint': 'Ons ontvang net u naam en e-posadres, en eers nadat u daartoe ingestem het.',
  'auth.callback.signingIn': 'Besig om u aan te meld…',
  'auth.callback.failedTitle': 'Daardie aanmelding is nie voltooi nie',
  'auth.callback.failedLead': 'Niks op u rekening het verander nie. Probeer weer, of meld aan met u e-posadres en wagwoord.',
  'auth.callback.backToSignIn': 'Terug na aanmelding',
  'auth.callback.error.STATE_INVALID': 'Daardie aanmeldskakel is reeds gebruik of het verval. Begin asseblief weer.',
  'auth.callback.error.PROVIDER_REFUSED': 'U het die aanmelding by {{provider}} gekanselleer. Niks is met ons gedeel nie.',
  'auth.callback.error.PROVIDER_DISABLED': 'Aanmelding met {{provider}} is afgeskakel. Gebruik asseblief u e-posadres en wagwoord.',
  'auth.callback.error.NO_CODE': '{{provider}} het ons sonder n antwoord teruggestuur. Probeer asseblief weer.',
  'auth.callback.error.TOKEN_EXCHANGE_FAILED': 'Ons kon nie die aanmelding met {{provider}} voltooi nie. Probeer asseblief oor n oomblik weer.',
  'auth.callback.error.NO_ID_TOKEN': '{{provider}} het nie bevestig wie u is nie. Probeer asseblief weer.',
  'auth.callback.error.ID_TOKEN_INVALID': 'Ons kon nie die antwoord van {{provider}} verifieer nie, en het dus gestop. Probeer asseblief weer.',
  'auth.callback.error.NONCE_MISMATCH': 'Ons kon nie daardie aanmelding met die een wat u begin het pas nie. Begin asseblief weer.',
  'auth.callback.error.PROVIDER_UNREACHABLE': '{{provider}} kon nie bereik word nie. Probeer asseblief oor n oomblik weer.',
  'auth.callback.error.EMAIL_REQUIRED': 'U {{provider}}-rekening het geen e-posadres daarop nie, en ons het een nodig. Registreer asseblief eerder met u e-posadres.',
  'auth.callback.error.EMAIL_NOT_VERIFIED': '{{provider}} het daardie e-posadres nog nie bevestig nie. Bevestig dit daar en probeer dan weer.',
  'auth.callback.error.DOMAIN_NOT_ALLOWED': 'Aanmelding is tot sekere e-posdomeine beperk, en u sn is nie een daarvan nie.',
  'auth.callback.error.LINK_REQUIRES_VERIFICATION': 'n Rekening gebruik reeds daardie e-posadres maar het dit nog nie bevestig nie. Meld aan met u wagwoord en bevestig u e-pos, dan kan u {{provider}} gebruik.',
  'auth.callback.error.WRONG_DOMAIN': 'Daardie rekening kan nie hier aanmeld nie.',
  'auth.callback.error.ACCOUNT_SUSPENDED': 'Daardie rekening is opgeskort. Vra asseblief u administrateur.',
  'auth.callback.error.PLATFORM_SIGNUP_BLOCKED': 'Personeelrekeninge kan nie op hierdie manier geskep word nie.',
  'auth.callback.error.SIGNUP_FAILED': 'Ons kon nie u rekening skep nie. Registreer asseblief eerder met u e-posadres.',
  'auth.callback.error.SIGN_IN_FAILED': 'Daardie aanmelding het nie gewerk nie. Probeer asseblief weer, of gebruik u e-posadres en wagwoord.',
  'auth.callback.error.EXCHANGE_FAILED': 'Daardie aanmeldskakel is reeds gebruik of het verval. Begin asseblief weer.',
  'auth.signup.form.referralCode': 'Verwysingskode',
  'auth.signup.form.referralCodeHint': 'As iemand jou bekendgestel het, sit hul kode hier. Laat dit leeg as nie.',
  'auth.signup.form.referralCodePlaceholder': 'Byvoorbeeld THAN7K4Q',
  'auth.signup.form.referredBy': 'Verwys deur {{name}}.',
  'auth.signup.form.referralUnknown': 'Ons herken nie daardie kode nie. Jy kan steeds sonder dit voortgaan.',
  'agreements.dpa.title': 'Voordat jy begin: ons dataverwerkingsooreenkoms',
  'agreements.dpa.titleUpdated': 'Ons dataverwerkingsooreenkoms is bygewerk',
  'agreements.dpa.why': 'Jou kontakte, gesprekke en oproepopnames is jou kliënte se persoonlike inligting. Kragtens POPIA versorg ons dit namens jou, en hierdie ooreenkoms stel uiteen hoe: wat ons daarmee doen, hoe ons dit veilig hou en wat daarmee gebeur wanneer jy vertrek.',
  'agreements.dpa.updated': 'Bygewerk op {{date}}. Lees dit asseblief en aanvaar dit weer om voort te gaan.',
  'agreements.dpa.read': 'Lees die dataverwerkingsooreenkoms (weergawe {{version}})',
  'agreements.dpa.confirm': 'Ek het die dataverwerkingsooreenkoms gelees en aanvaar dit namens {{org}}.',
  'agreements.dpa.accept': 'Aanvaar en gaan voort',
  'agreements.failed': 'Kon nie jou aanvaarding aanteken nie.',
  'agreements.impersonating': 'Iemand in die organisasie moet dit aanvaar. ’n Platformoperateur wat inkyk, kan dit nie namens hulle aanvaar nie.',
  'agreements.impersonatingBanner': 'Hierdie organisasie het nog nie die huidige dataverwerkingsooreenkoms aanvaar nie. Sy mense word gevra om dit te doen voordat hulle die konsole kan gebruik.',
  'onboarding.steps.agreement.label': 'Ooreenkoms',
  'onboarding.steps.agreement.title': 'Aanvaar ons dataverwerkingsooreenkoms',
  'onboarding.steps.agreement.done': 'Op {{date}} deur {{who}} aanvaar.',
  'agreements.agentWaits': '’n Eienaar of administrateur in jou organisasie moet dit aanvaar voordat die konsole gebruik kan word. Laat een van hulle weet.',
  'auth.callback.codeTitle': 'Nog een stap',
  'auth.callback.error.TWO_FACTOR_SETUP_REQUIRED': 'Hierdie rekening moet eers tweestap-aanmelding opstel. Meld aan met jou e-pos en wagwoord, en jy sal gewys word hoe.',
  'onboarding.steps.name.form.businessName': 'Besigheidsnaam',
  'onboarding.steps.name.form.businessNameHint': 'Hoe kliënte jou ken. Dit verskyn op fakture en in boodskappe, en jy kan dit later verander onder Instellings, Organisasie.',
  'onboarding.steps.name.form.businessNamePlaceholder': 'bv. Sunrise Salon'
};

export default signIn;
